import { memo, useMemo, useRef, useEffect, useCallback, useState } from "react";
import { setHours, startOfDay, subDays, addDays } from "date-fns";
import { Maximize2, Minimize2 } from "lucide-react";
import { IcGarbageTruck, IcProfile } from "@racoon/icons";

import { useLazyListActiveTrucksWithRoutesQuery } from "~/shared/api/routes/route.grpc";
import { useAllTruckStatusesQuery, useListTrucksQuery } from "~/shared/api/truck/truck.grpc";
import { useListTeamsQuery } from "~/shared/api/team/team.grpc";
import { useListUsersQuery } from "~/shared/api/user/user.grpc";
import { TRUCK_TYPE_ORDER, isTruckArchived, truckTypeOf } from "~/shared/lib/truck-type";
import { NO_TEAM } from "~/shared/lib/driver-teams";
import {
  useUpdateDriverOnShiftMutation,
  useListDriverOnShiftQuery,
} from "~/shared/api/shift/driver-on-shift.grpc";
import { toTimestamp, fromTimestamp } from "@racoon/lib";
import { ranUntilByShift, shiftSpansById } from "./shift-span";
import { isPastDay } from "~/shared/api/cache";
import { useDelayedFlag } from "~/shared/hooks/use-delayed-flag";
import { useHeightTransition } from "~/shared/hooks/use-height-transition";
import { shiftWindow } from "~/shared/lib/shift-status";
import { GanttSchedule, GanttScheduleRef, Vehicle } from "./gant-chart";
import { GanttLayout, SourceItem, byTeam, byTruckType, jobsOnDay, noDriverKey } from "./gantt-rows";
import { useInfoPanel } from "~/features/info-panel/model/useInfoPanel";
import { Namespace, useLang, useT } from "@racoon/i18n";
import { MapObject } from "~/features/map-view/ui/map-view";
import {
  Button,
  cn,
  DatePicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@racoon/ui";
import { getRouteCategoryMap } from "~/shared/lib/translationMaps";
import { useGetOrganizationSettingsQuery } from "~/shared/api/organization/organization.grpc";
import PickupStatusPanel from "./pickup-status/pickup-status.panel";
import {
  dayRange,
  useDayRoutes,
  type DashboardFilters,
} from "~/shared/lib/pickup-status/use-pickup-status";
import { ConnectProto } from "@racoon/api";
import { useActiveChildOrgs } from "~/shared/api/organization/use-active-child-orgs";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useHeldWhileLoading } from "~/shared/hooks/use-held-while-loading";
import { ModeTabs } from "~/shared/ui/mode-tabs";

// Span the schedule opens on, and scrolls to when a day is picked in the calendar.
function dayWindow(day: Date) {
  const midnight = startOfDay(day);
  return { start: setHours(midnight, 5), end: setHours(midnight, 19) };
}

// Collapse the DriverOnShift status enum onto the three shift states the schedule
// shows, each backed by an i18n key in Namespace.Routes.
function shiftStatusI18nKey(
  status?: ConnectProto.DriverOnShiftStatus,
): "shiftActive" | "shiftCompleted" | "shiftPlanned" | undefined {
  switch (status) {
    case ConnectProto.DriverOnShiftStatus.ENDED:
      return "shiftCompleted";
    case ConnectProto.DriverOnShiftStatus.NOT_APPROVED:
      return "shiftPlanned";
    case ConnectProto.DriverOnShiftStatus.ACTIVE:
    case ConnectProto.DriverOnShiftStatus.TRUCK_IDLE:
    case ConnectProto.DriverOnShiftStatus.DRIVER_IDLE:
    case ConnectProto.DriverOnShiftStatus.CONFIRMATION:
    case ConnectProto.DriverOnShiftStatus.ENROUTE:
    case ConnectProto.DriverOnShiftStatus.ON_SERVICE:
    case ConnectProto.DriverOnShiftStatus.IDLE:
      return "shiftActive";
    default:
      return undefined;
  }
}

// Tailwind text-color class for the shift status in the hover header, per shift
// state. Values come from the shared tailwind config (and are safelisted there).
const SHIFT_STATUS_CLASS: Record<"shiftActive" | "shiftCompleted" | "shiftPlanned", string> = {
  shiftActive: "text-brand-green",
  shiftPlanned: "text-brand-blue",
  shiftCompleted: "text-status-gray",
};

type ShiftStatusInfo = { label?: string; className?: string };

function driverFullName(driver?: { firstName?: string; lastName?: string }): string {
  return driver ? `${driver.firstName ?? ""} ${driver.lastName ?? ""}`.trim() : "";
}

// Categories whose routes collect many pickups — listing every location name would
// overflow the gantt bar, so they show only the job currently in progress.
const SINGLE_LABEL_CATEGORIES = new Set<ConnectProto.RouteCategory>([
  ConnectProto.RouteCategory.MSW,
  ConnectProto.RouteCategory.LIQUID,
  ConnectProto.RouteCategory.DISINFECTION,
]);

// Label shown inside the gantt bar. For the categories above only the job the driver
// is currently on is named (on-service wins over en-route); with nothing in progress
// the bar carries just the title and the counter. Other categories keep the full list,
// since they carry just a couple of jobs.
function routeSegmentLabel(r: ConnectProto.Route): string {
  const jobs = r.jobs ?? [];
  if (SINGLE_LABEL_CATEGORIES.has(r.routeCategory)) {
    const activeJob =
      jobs.find((j) => j.status === ConnectProto.JobStatus.ON_SERVICE) ??
      jobs.find((j) => j.status === ConnectProto.JobStatus.ENROUTE);
    return activeJob?.pickupLocation?.name || "";
  }
  return jobs
    .map((j) => j.pickupLocation?.name || "")
    .filter(Boolean)
    .join(" | ");
}

// Decoded messages default a missing id to 0n, which no shift has.
const shiftIdOf = (r: ConnectProto.Route) => r.driverOnShift?.id || r.driverOnShiftId || undefined;

// Map a Route to a gantt item. For active-truck routes the truck is known upfront
// (pass `truckIdOverride`); for standalone listRoutes routes it is reached through
// the route's driver-on-shift (routes have no top-level truck).
function mapRouteToItem(
  r: ConnectProto.Route,
  resolveShiftStatus: (status?: ConnectProto.DriverOnShiftStatus) => ShiftStatusInfo,
  truckIdOverride?: string | bigint,
  // Active-truck routes carry only the shift status on `route.driverOnShift`; the
  // driver lives on the truck's current shift, so callers can pass it as a fallback.
  // `caption` names the hover frame only: a truck's drivers can't tell whose route it is.
  // `driverId` keys the row of a driver known only by id.
  who: { driver?: ConnectProto.User; caption?: ConnectProto.User; driverId?: string } = {},
): SourceItem | null {
  const truckId = truckIdOverride ?? r.driverOnShift?.truck?.id ?? r.driverOnShift?.truckId;
  if (truckId === undefined) return null;
  const driver = r.driverOnShift?.driver ?? who.driver;
  const dosId = shiftIdOf(r);
  const shiftStatus = resolveShiftStatus(r.driverOnShift?.status);
  return {
    id: String(r.id),
    vehicleId: String(truckId),
    status: r.status,
    title: `M${r.id}`,
    segmentLabel: routeSegmentLabel(r),
    // The counter is always shown, even on routes with no jobs yet ("0/0").
    countBadge: `${(r.jobs ?? []).filter((j) => j.factEndAt).length}/${(r.jobs ?? []).length}`,
    jobCount: (r.jobs ?? []).length,
    start: fromTimestamp(r.factStartAt ?? r.planStartAt) || new Date(),
    end: fromTimestamp(r.factEndAt ?? r.planEndAt) || new Date(),
    routeCategory: r.routeCategory,
    ownerOrganizationId: String(r.ownerOrganizationId),
    driverOnShiftId: dosId !== undefined ? String(dosId) : undefined,
    shiftStatusLabel: shiftStatus.label,
    shiftStatusClass: shiftStatus.className,
    shiftCaption: driverFullName(driver ?? who.caption),
    driverKey: driver?.id ? String(driver.id) : (who.driverId ?? noDriverKey(truckId)),
    shiftVisibleToAi: r.driverOnShift?.visibleToAi,
  };
}

type ScheduleTab = "pickupStatus" | "schedule";
type GanttMode = "truck" | "driver";
export type MapTruckPick = { truckIds: Set<string>; label: string; clear: () => void };

const noop = () => {};

const MAX_DAYS_AHEAD = 7;

// Every tab reaches over the header's bottom line, so switching tabs moves nothing; the
// inactive tab's transparent bottom edge lets the line show, the active one's white edge hides it.
const TAB_TRIGGER =
  "h-[41px] -mb-px pb-[7px] [background-clip:padding-box] !px-[11px] !font-bold !tracking-normal !text-text-subtle data-[state=active]:!text-text-body";
// Figma's header controls: 20px icons, a 1.5 stroke on the day arrows, body-colour text.
const DAY_NAV_BUTTON = "!p-0 h-8 w-8 shrink-0 text-[#151A24] [&>svg]:size-5 [&>svg]:stroke-[1.5]";
const TAB_FADE =
  "data-[state=active]:animate-in data-[state=active]:fade-in-0 duration-300 motion-reduce:data-[state=active]:animate-none";
const SELECT_FIGMA = "!w-40 !px-2 !rounded-lg text-text-body [&>svg]:size-5 [&>svg]:opacity-100";

type Props = {
  filters: DashboardFilters;
  onFiltersChange: (patch: Partial<DashboardFilters>) => void;
  /** The day's jobs are still loading (the page fetches them for the map and the status tab). */
  jobsLoading?: boolean;
  /** The trucks of the truck type or team picked in the schedule; null when none is picked. */
  onMapTrucks?: (pick: MapTruckPick | null) => void;
};

// Drops the words every name shares («ТОО "X" - »), leaving the part that tells divisions apart.
function shortNames(names: string[]): string[] {
  if (names.length < 2) return names;
  let prefix = names.reduce((a, b) => {
    let i = 0;
    while (i < a.length && a[i] === b[i]) i++;
    return a.slice(0, i);
  });
  prefix = prefix.slice(0, prefix.search(/\s\S*$/) + 1);
  return names.map((n) => n.slice(prefix.length).replace(/^[\s\-–—]+/, "") || n);
}

function RoutesSchedulePage({ filters, onFiltersChange, jobsLoading = false, onMapTrucks }: Props) {
  const t = useT();
  const { open, close, infoId, infoType } = useInfoPanel();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<ScheduleTab>("schedule");
  // The two tabs differ in height; the card eases between them instead of jumping.
  const tabBodyRef = useRef<HTMLDivElement>(null);
  useHeightTransition(tabBodyRef, activeTab);
  const [ganttMode, setGanttMode] = useState<GanttMode>("truck");
  const [truckTypeFilter, setTruckTypeFilter] = useState<ConnectProto.TruckType | "all">("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const { day: selectedDay, category: selectedCategory, divisionId } = filters;
  const { activeCompanyId: selectedCompanyId } = useAuthSession();
  const { children: divisions, isLoading: divisionsLoading } = useActiveChildOrgs();
  const divisionLabels = useMemo(() => shortNames(divisions.map((org) => org.name)), [divisions]);
  const selectedDivision = divisions.findIndex((org) => String(org.id) === String(divisionId));

  const ganttRef = useRef<GanttScheduleRef>(null);

  const [updateDriverOnShift] = useUpdateDriverOnShiftMutation();

  const [listActiveTrucks, { data: activeTrucksData }] = useLazyListActiveTrucksWithRoutesQuery();
  const statusList = activeTrucksData?.trucksWithRoutes;

  // Truck statuses carry the driver on their current/latest shift; the active-trucks
  // routes feed doesn't always embed it, so this is the reliable driver source.
  const { data: truckStatusesResponse } = useAllTruckStatusesQuery(
    { ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [] },
    { skip: !selectedCompanyId },
  );
  const truckStatuses = truckStatusesResponse?.truckStatuses;

  // The routes include the divisions', so their trucks, teams and drivers are needed too.
  const orgIds = useMemo(
    () =>
      selectedCompanyId
        ? [BigInt(selectedCompanyId), ...divisions.map((org) => BigInt(org.id))]
        : [],
    [selectedCompanyId, divisions],
  );
  const fleetQuery = useListTrucksQuery(
    { ownerOrganizationIds: orgIds },
    { skip: !selectedCompanyId || divisionsLoading },
  );
  const fleet = fleetQuery.data;
  const { typeByTruckId, activeByType } = useMemo(() => {
    const typeByTruckId = new Map<string, ConnectProto.TruckType>();
    const activeByType = new Map<ConnectProto.TruckType, number>();
    fleet?.trucks.forEach((truck) => {
      const type = truckTypeOf(truck);
      typeByTruckId.set(String(truck.id), type);
      if (!isTruckArchived(truck)) activeByType.set(type, (activeByType.get(type) ?? 0) + 1);
    });
    return { typeByTruckId, activeByType };
  }, [fleet]);

  const { lang } = useLang();
  const teamsQuery = useListTeamsQuery(
    { ownerOrganizationIds: orgIds, userRoles: [ConnectProto.UserRole.DRIVER] },
    { skip: !selectedCompanyId || divisionsLoading || ganttMode !== "driver" },
  );
  // Inactive ones too: their routes still name them, in both views.
  const { currentData: orgDrivers } = useListUsersQuery(
    { ownerOrganizationIds: orgIds, roles: [ConnectProto.UserRole.DRIVER] },
    { skip: !selectedCompanyId || divisionsLoading },
  );
  const driverById = useMemo(
    () => new Map(orgDrivers?.users.map((user) => [String(user.id), user])),
    [orgDrivers],
  );
  const activeDriverIds = useMemo(
    () =>
      orgDrivers?.users
        .filter((user) => user.status === ConnectProto.UserStatus.ACTIVE)
        .map((user) => String(user.id)),
    [orgDrivers],
  );
  const teams = useMemo(
    () =>
      [...(teamsQuery.currentData?.teams ?? [])].sort((a, b) => a.name.localeCompare(b.name, lang)),
    [teamsQuery.currentData, lang],
  );
  // A picked team that is gone (deleted, or another company's) falls back to all.
  const activeTeamFilter =
    teamsQuery.isError ||
    (teamsQuery.currentData &&
      teamFilter !== "all" &&
      teamFilter !== NO_TEAM &&
      !teams.some((team) => String(team.id) === teamFilter))
      ? "all"
      : teamFilter;

  // Nothing is planned further ahead; the past stays open since every day loads on its own.
  const maxDay = startOfDay(addDays(new Date(), MAX_DAYS_AHEAD));

  const { data: settings } = useGetOrganizationSettingsQuery(
    {},
    {
      skip: !selectedCompanyId,
    },
  );
  const isAiEnabled = settings?.aiEnabled ?? false;

  // Only the picked day: a fixed multi-day span cost tens of MB on every load. Today's routes
  // are the live feed above, so they need no query of their own.
  const dayKey = startOfDay(selectedDay).getTime();
  const dayArgs = useMemo(() => {
    const { from, last } = dayRange(dayKey);
    return {
      ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [],
      datetimeFilter: { from: toTimestamp(from), to: toTimestamp(last) },
    };
  }, [dayKey, selectedCompanyId]);
  // From the day before: a night shift that began yesterday evening still owns today's routes.
  const shiftArgs = useMemo(
    () => ({
      ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [],
      dateTimeFilter: {
        from: toTimestamp(subDays(new Date(dayKey), 1)),
        to: toTimestamp(addDays(new Date(dayKey), 1)),
      },
    }),
    [dayKey, selectedCompanyId],
  );
  const shiftQuery = useListDriverOnShiftQuery(shiftArgs, { skip: !selectedCompanyId });
  const shiftResponse = useHeldWhileLoading(
    shiftQuery.currentData,
    String(selectedCompanyId),
    shiftQuery.isError,
  );
  // The request reaches back a day for overnight shifts; a shift that ended before this day
  // would only add an empty truck row.
  const driverOnShiftList = useMemo(() => {
    const { from, to } = dayRange(dayKey);
    return shiftResponse?.driverOnShifts.filter((dos) => {
      const { start, end } = shiftWindow(dos);
      return (!start || start < to) && (!end || end >= from);
    });
  }, [shiftResponse, dayKey]);
  // Only answers to the current company and day: a held one would pair stale trucks or
  // shifts with fresh jobs. The panel keeps its last row while these load.
  const freshTrucks = fleetQuery.currentData?.trucks;
  const freshShifts = shiftQuery.currentData?.driverOnShifts;
  const pickupFleet = useMemo(
    () =>
      freshTrucks && freshShifts && driverOnShiftList
        ? { trucks: freshTrucks, dayShifts: driverOnShiftList, allShifts: freshShifts }
        : undefined,
    [freshTrucks, freshShifts, driverOnShiftList],
  );
  const isToday = dayKey === startOfDay(new Date()).getTime();
  const past = isPastDay(new Date(dayKey));
  const dayQuery = useDayRoutes(dayArgs, past, !selectedCompanyId || isToday);
  const heldDay = useHeldWhileLoading(
    dayQuery.currentData,
    String(selectedCompanyId),
    dayQuery.isError,
  );
  const dayResponse = isToday ? undefined : heldDay;
  const ganttFetching =
    (!isToday && dayQuery.isFetching) ||
    shiftQuery.isFetching ||
    teamsQuery.isFetching ||
    (!fleet && (divisionsLoading || fleetQuery.isFetching));
  // The live feed is today's; on any other day it would add today's routes and trucks.
  const liveTrucks = isToday ? statusList : undefined;
  const ganttDimmed = useDelayedFlag(ganttFetching);
  const dayLoading = useDelayedFlag(jobsLoading || ganttFetching);
  const rangeRoutes = useMemo(() => dayResponse?.routes ?? [], [dayResponse]);

  const [aiVisibilityOverrides, setAiVisibilityOverrides] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (selectedCompanyId) {
      listActiveTrucks({
        ownerOrganizationIds: [BigInt(selectedCompanyId)],
      });
    }
  }, [selectedCompanyId]);

  const vehicles = useMemo(() => {
    const vehiclesMap = new Map<string, any>();

    liveTrucks?.forEach((x) => {
      if (!x.truck || !x.routes || x.routes.length === 0) return;

      const truckShift = x.routes[0]?.driverOnShift;
      const dosId = truckShift?.id;
      const truckId = String(x.truck.id);

      let isVisible = !!truckShift?.visibleToAi;
      if (dosId && typeof aiVisibilityOverrides[Number(dosId)] !== "undefined") {
        isVisible = aiVisibilityOverrides[Number(dosId)];
      }

      vehiclesMap.set(truckId, {
        id: truckId,
        label: x.truck.plateNumber,
        visible_to_ai: isVisible,
        driver_on_shift_id: dosId,
      });
    });

    driverOnShiftList?.forEach((dos: any) => {
      if (!dos.truck) return;

      const truckId = String(dos.truck.id);
      const dosId = Number(dos.id);

      let isVisible = !!(dos.visible_to_ai || dos.visibleToAi);
      if (typeof aiVisibilityOverrides[dosId] !== "undefined") {
        isVisible = aiVisibilityOverrides[dosId];
      }
      if (vehiclesMap.has(truckId)) {
        const existing = vehiclesMap.get(truckId);
        vehiclesMap.set(truckId, {
          ...existing,
          visible_to_ai: isVisible,
          driver_on_shift_id: dosId,
        });
      } else {
        vehiclesMap.set(truckId, {
          id: truckId,
          label: dos.truck.plate_number || dos.truck.plateNumber,
          visible_to_ai: isVisible,
          driver_on_shift_id: dosId,
        });
      }
    });

    // Ensure a row exists for every truck referenced by past/future routes.
    rangeRoutes.forEach((r) => {
      const truck = r.driverOnShift?.truck;
      if (!truck) return;
      const truckId = String(truck.id);
      if (vehiclesMap.has(truckId)) return;

      const dosId = r.driverOnShift?.id ? Number(r.driverOnShift.id) : undefined;

      let isVisible = !!r.driverOnShift?.visibleToAi;
      if (dosId !== undefined && typeof aiVisibilityOverrides[dosId] !== "undefined") {
        isVisible = aiVisibilityOverrides[dosId];
      }

      vehiclesMap.set(truckId, {
        id: truckId,
        label: truck.plateNumber,
        visible_to_ai: isVisible,
        driver_on_shift_id: dosId,
      });
    });

    return Array.from(vehiclesMap.values());
  }, [liveTrucks, driverOnShiftList, rangeRoutes, aiVisibilityOverrides]);

  const resolveShiftStatus = useCallback(
    (status?: ConnectProto.DriverOnShiftStatus): ShiftStatusInfo => {
      const key = shiftStatusI18nKey(status);
      if (!key) return {};
      return { label: t(Namespace.Routes, key), className: SHIFT_STATUS_CLASS[key] };
    },
    [t],
  );

  const items = useMemo(() => {
    const isApproved = (
      status?: ConnectProto.RouteStatus,
      dosStatus?: ConnectProto.DriverOnShiftStatus,
    ) =>
      status !== ConnectProto.RouteStatus.NOT_APPROVED &&
      dosStatus !== ConnectProto.DriverOnShiftStatus.NOT_APPROVED;

    // A route's own shift doesn't always embed the driver (the active-trucks feed
    // populates it on some routes of a shift but not others). Index the driver by
    // shift id from every source we have — the driver-on-shift list, the truck
    // statuses' current/latest shift, and the shifts embedded on the active feed and
    // range routes — so any route of a shift can resolve its driver as long as it
    // arrived somewhere in the responses.
    const driverByDosId = new Map<string, ConnectProto.User>();
    type TruckShift = { driver: ConnectProto.User; start?: Date; end?: Date };
    const shiftsByTruckId = new Map<string, TruckShift[]>();
    const indexShift = (dos?: ConnectProto.DriverOnShift) => {
      if (!dos?.driver) return;
      if (dos.id !== undefined && !driverByDosId.has(String(dos.id))) {
        driverByDosId.set(String(dos.id), dos.driver);
      }
      const truckId = dos.truck?.id ?? dos.truckId;
      if (truckId === undefined) return;
      const shifts = shiftsByTruckId.get(String(truckId)) ?? [];
      shifts.push({ driver: dos.driver, ...shiftWindow(dos) });
      shiftsByTruckId.set(String(truckId), shifts);
    };
    driverOnShiftList?.forEach(indexShift);
    // Truck statuses reliably carry the driver on current/latest shift, keyed by truck.
    truckStatuses?.forEach((s) => {
      indexShift(s.currentShift);
      indexShift(s.latestShift);
    });
    // Key route-sourced drivers by the same shift id `driverFor` looks up with — the
    // embedded shift's own `id` isn't always set (only `route.driverOnShiftId` is), so
    // fall back to it, otherwise the active-feed driver would never be indexed.
    const indexRouteDriver = (r: ConnectProto.Route) => {
      const dosId = shiftIdOf(r);
      const driver = r.driverOnShift?.driver;
      if (dosId !== undefined && driver && !driverByDosId.has(String(dosId))) {
        driverByDosId.set(String(dosId), driver);
      }
    };
    liveTrucks?.forEach((x) => x.routes?.forEach(indexRouteDriver));
    rangeRoutes.forEach(indexRouteDriver);
    // A route with no known shift is its truck's driver's only if one driver's shift there covers
    // its start (a day and a night shift share trucks); otherwise it gets the no-driver row.
    const driverFor = (r: ConnectProto.Route, truckId?: string | bigint) => {
      const dosId = shiftIdOf(r);
      const driverId = r.driverOnShift?.driverId;
      const shiftDriver =
        r.driverOnShift?.driver ??
        (dosId !== undefined ? driverByDosId.get(String(dosId)) : undefined) ??
        (driverId !== undefined ? driverById.get(String(driverId)) : undefined);
      if (shiftDriver) return { driver: shiftDriver };
      // Known by id only: still their route, not the truck's current driver's.
      if (driverId) return { driverId: String(driverId) };
      const tId = truckId ?? r.driverOnShift?.truck?.id ?? r.driverOnShift?.truckId;
      const shifts = shiftsByTruckId.get(String(tId)) ?? [];
      const at = fromTimestamp(r.factStartAt ?? r.planStartAt);
      const covering = shifts.filter(
        (sh) => at && (!sh.start || sh.start <= at) && (!sh.end || at <= sh.end),
      );
      const only = (list: TruckShift[]) =>
        new Set(list.map((sh) => String(sh.driver.id))).size === 1 ? list[0].driver : undefined;
      const coveringDriver = only(covering);
      // A shift we know of but can't name a driver for (e.g. a driverless one) isn't the truck's.
      return dosId === undefined ? { driver: coveringDriver, caption: coveringDriver } : {};
    };

    const byId = new Map<string, SourceItem>();

    // Today: live active-truck feed. Truck comes from the TruckWithRoute wrapper.
    liveTrucks?.forEach((x) => {
      const truck = x.truck;
      if (!truck || !x.routes) return;
      x.routes.forEach((r) => {
        if (!isApproved(r.status, r.driverOnShift?.status)) return;
        const item = mapRouteToItem(r, resolveShiftStatus, truck.id, driverFor(r, truck.id));
        if (item) byId.set(item.id, item);
      });
    });

    // Past/future days: pulled from listRoutes. Today's live entries win on id collision.
    rangeRoutes.forEach((r) => {
      if (!isApproved(r.status, r.driverOnShift?.status)) return;
      if (byId.has(String(r.id))) return;
      const item = mapRouteToItem(r, resolveShiftStatus, undefined, driverFor(r));
      if (item) byId.set(item.id, item);
    });

    const shiftRoutes = [
      ...(liveTrucks ?? []).flatMap((x) => x.routes ?? []),
      ...rangeRoutes,
    ].filter((r) => isApproved(r.status, r.driverOnShift?.status));
    const spans = shiftSpansById(
      [
        ...(driverOnShiftList ?? []),
        ...(truckStatuses ?? []).flatMap((s) => [s.currentShift, s.latestShift]),
        ...shiftRoutes.map(
          (r) => r.driverOnShift && { ...r.driverOnShift, id: shiftIdOf(r) ?? r.driverOnShift.id },
        ),
      ],
      ranUntilByShift(
        shiftRoutes.map((r): [string | undefined, ConnectProto.Route] => {
          const dosId = shiftIdOf(r);
          return [dosId !== undefined ? String(dosId) : undefined, r];
        }),
      ),
    );
    return Array.from(byId.values())
      .filter((it) => divisionId === null || it.ownerOrganizationId === String(divisionId))
      .map((it) => {
        const span =
          it.driverOnShiftId !== undefined ? spans.get(String(it.driverOnShiftId)) : undefined;
        // Neighbouring days' routes are loaded too; the badge counts only the picked day's.
        const jobCount = jobsOnDay(it, new Date(dayKey));
        return { ...it, jobCount, ...(span && { shiftSpan: span }) };
      });
  }, [
    dayKey,
    liveTrucks,
    rangeRoutes,
    driverOnShiftList,
    truckStatuses,
    driverById,
    divisionId,
    resolveShiftStatus,
  ]);

  // The driver view's rows: everyone on a shift that day, and anyone driving one of its routes.
  const drivers = useMemo(() => {
    const byKey = new Map<string, Vehicle>();
    const truckRows = new Map(vehicles.map((v) => [String(v.id), v]));
    const keptShift = new Map<string, ConnectProto.DriverOnShift>();
    const visibleByDosId = new Map(
      driverOnShiftList?.map((dos) => [String(dos.id), !!dos.visibleToAi]),
    );
    driverOnShiftList?.forEach((dos) => {
      if (!dos.driver || !dos.truck) return;
      const id = String(dos.driver.id);
      const dosId = Number(dos.id);
      // One row per driver: an unfinished shift (its AI toggle, its truck) over a finished one.
      const kept = keptShift.get(id);
      if (kept && (!kept.completedAt || dos.completedAt)) return;
      keptShift.set(id, dos);
      byKey.set(id, {
        id,
        label: driverFullName(dos.driver),
        truckId: String(dos.truck.id),
        driver_on_shift_id: dosId,
        visible_to_ai: aiVisibilityOverrides[dosId] ?? !!dos.visibleToAi,
      });
    });
    items.forEach((it) => {
      if (byKey.has(it.driverKey)) return;
      const truck = truckRows.get(it.vehicleId);
      byKey.set(it.driverKey, {
        id: it.driverKey,
        label:
          it.driverKey === noDriverKey(it.vehicleId)
            ? `${truck?.label ?? ""} · ${t(Namespace.Routes, "noDriver")}`
            : it.shiftCaption || `${t(Namespace.Routes, "driver")} #${it.driverKey}`,
        truckId: it.vehicleId,
        driver_on_shift_id: it.driverOnShiftId,
        // The item's own shift, as the toggle handler reads it; the truck row's is another shift's.
        visible_to_ai:
          it.driverOnShiftId !== undefined
            ? (aiVisibilityOverrides[Number(it.driverOnShiftId)] ??
              visibleByDosId.get(String(it.driverOnShiftId)) ??
              it.shiftVisibleToAi)
            : undefined,
      });
    });
    return [...byKey.values()];
  }, [vehicles, driverOnShiftList, items, aiVisibilityOverrides, t]);

  const truckTypeLabel = useCallback(
    (type: ConnectProto.TruckType) =>
      t(
        Namespace.Routes,
        `ganttTruckTypes.${(ConnectProto.TruckType[type] ?? "UNDEFINED") as keyof typeof ConnectProto.TruckType}`,
      ),
    [t],
  );

  const layout = useMemo((): GanttLayout & { truckIds?: Set<string> } => {
    const rowsWithItems = (rows: Vehicle[], rowOf: (it: SourceItem) => string) => {
      if (divisionId === null) return rows;
      const ids = new Set(items.map(rowOf));
      return rows.filter((row) => ids.has(row.id));
    };
    if (ganttMode === "truck") {
      // Without the types every truck would flash under «type not set» first.
      if (!fleet && !fleetQuery.isError) return { rows: [], items: [] };
      return byTruckType({
        trucks: rowsWithItems(vehicles, (it) => it.vehicleId),
        items,
        typeOf: (id) => typeByTruckId.get(id) ?? ConnectProto.TruckType.UNDEFINED,
        totals: activeByType,
        filter: truckTypeFilter,
        label: truckTypeLabel,
      });
    }
    // Without the teams every driver would flash under «no team» first.
    if (!teamsQuery.currentData && !teamsQuery.isError) return { rows: [], items: [] };
    // On a driver's row the shift is told apart by its truck.
    const plates = new Map(vehicles.map((v) => [String(v.id), v.label]));
    return byTeam({
      drivers: rowsWithItems(drivers, (it) => it.driverKey),
      items: items.map((it) => ({ ...it, shiftCaption: plates.get(it.vehicleId) })),
      teams,
      filter: activeTeamFilter,
      noTeamLabel: t(Namespace.Common, "noTeam"),
      activeDriverIds,
    });
  }, [
    ganttMode,
    vehicles,
    drivers,
    items,
    divisionId,
    typeByTruckId,
    activeByType,
    truckTypeFilter,
    truckTypeLabel,
    teams,
    teamsQuery.currentData,
    teamsQuery.isError,
    fleet,
    fleetQuery.isError,
    activeDriverIds,
    activeTeamFilter,
    t,
  ]);

  // Only while the pick is on screen: the other tab and the other mode don't show it.
  const pickedTruckIds = useMemo(() => {
    if (activeTab !== "schedule") return null;
    if (ganttMode === "truck") {
      if (truckTypeFilter === "all") return null;
      const ids = [...typeByTruckId].filter(([, type]) => type === truckTypeFilter);
      return new Set(ids.map(([id]) => id));
    }
    if (activeTeamFilter === "all") return null;
    return layout.truckIds ?? null;
  }, [activeTab, ganttMode, truckTypeFilter, typeByTruckId, activeTeamFilter, layout]);
  // The same Set while the ids stay, so a live refresh doesn't re-render the dashboard.
  const mapTruckKey = pickedTruckIds && [...pickedTruckIds].sort().join(",");
  const mapTruckIds = useMemo(() => pickedTruckIds, [mapTruckKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const mapPick = useMemo((): MapTruckPick | null => {
    if (!mapTruckIds) return null;
    if (ganttMode === "truck") {
      return {
        truckIds: mapTruckIds,
        label: truckTypeFilter === "all" ? "" : truckTypeLabel(truckTypeFilter),
        clear: () => setTruckTypeFilter("all"),
      };
    }
    const team = teams.find((tm) => String(tm.id) === activeTeamFilter);
    return {
      truckIds: mapTruckIds,
      label: team?.name ?? (activeTeamFilter === NO_TEAM ? t(Namespace.Common, "noTeam") : ""),
      clear: () => setTeamFilter("all"),
    };
  }, [mapTruckIds, ganttMode, truckTypeFilter, truckTypeLabel, activeTeamFilter, teams, t]);
  useEffect(() => {
    if (!onMapTrucks) return;
    onMapTrucks(mapPick);
    return () => onMapTrucks(null);
  }, [mapPick, onMapTrucks]);

  // Clicking the open route or truck again closes it, like the panel's close button.
  const handleItemClick = useCallback(
    (payload: { type: MapObject["type"]; id: string }) => {
      if (payload.type === infoType && String(payload.id) === String(infoId)) close();
      else open(payload.type, payload.id);
    },
    [open, close, infoType, infoId],
  );

  // Moving a visible gantt re-lays out hundreds of bars synchronously, so it waits a frame:
  // the new date and the spinner paint first instead of after the freeze.
  const [movingWindow, setMovingWindow] = useState(false);
  const windowDay = useRef(dayKey);
  const handleDayChange = useCallback(
    (date?: Date) => {
      if (!date) return;
      windowDay.current = startOfDay(date).getTime();
      onFiltersChange({ day: date });
      const { start, end } = dayWindow(date);
      if (activeTab !== "schedule") {
        ganttRef.current?.setWindow(start, end);
        return;
      }
      setMovingWindow(true);
      requestAnimationFrame(() =>
        setTimeout(() => {
          ganttRef.current?.setWindow(start, end);
          setMovingWindow(false);
        }),
      );
    },
    [onFiltersChange, activeTab],
  );

  // Opens on the dashboard's day, which survives leaving the page; only today centres on now.
  const [initialWindow] = useState(() => dayWindow(selectedDay));
  // The day can also change from outside, e.g. at midnight.
  useEffect(() => {
    if (windowDay.current === dayKey) return;
    windowDay.current = dayKey;
    const { start, end } = dayWindow(new Date(dayKey));
    ganttRef.current?.setWindow(start, end);
  }, [dayKey]);

  const categoryMap = useMemo(() => getRouteCategoryMap(t), [t]);
  const categoryOptions = useMemo(
    () =>
      (Object.entries(categoryMap) as [string, string][]).filter(
        ([key]) => Number(key) !== ConnectProto.RouteCategory.UNDEFINED,
      ),
    [categoryMap],
  );

  const handleToggleAiVisibility = useCallback(
    async (driverOnShiftId: number) => {
      let currentVisible = false;

      if (typeof aiVisibilityOverrides[driverOnShiftId] !== "undefined") {
        currentVisible = aiVisibilityOverrides[driverOnShiftId];
      } else {
        const dosFromList = driverOnShiftList?.find((d: any) => Number(d.id) === driverOnShiftId);
        const fromRoute = items.find(
          (it) => it.driverOnShiftId === String(driverOnShiftId),
        )?.shiftVisibleToAi;
        if (dosFromList) {
          currentVisible = !!dosFromList.visibleToAi;
        } else if (fromRoute !== undefined) {
          // The row shows the route's own shift, so the toggle starts from it too.
          currentVisible = fromRoute;
        } else {
          const truckEntry = statusList?.find((x: any) => {
            const dos =
              (x.truck as any)?.current_shift ||
              (x.truck as any)?.currentShift ||
              x.routes?.[0]?.driver_on_shift ||
              (x.routes?.[0] as any)?.driverOnShift;
            return dos?.id ? Number(dos.id) === driverOnShiftId : false;
          });
          const dos =
            (truckEntry?.truck as any)?.currentShift || truckEntry?.routes?.[0]?.driverOnShift;
          if (dos) {
            currentVisible = !!dos.visibleToAi;
          }
        }
      }

      const nextVisible = !currentVisible;

      setAiVisibilityOverrides((prev) => ({
        ...prev,
        [driverOnShiftId]: nextVisible,
      }));

      try {
        await updateDriverOnShift({
          id: BigInt(driverOnShiftId),
          visibleToAi: nextVisible,
          ownerOrganizationId: BigInt(selectedCompanyId || 0),
        }).unwrap();
      } catch (error) {
        console.error("Failed to update AI visibility", error);
        setAiVisibilityOverrides((prev) => {
          const newState = { ...prev };
          delete newState[driverOnShiftId];
          return newState;
        });
      }
    },
    [statusList, driverOnShiftList, items, aiVisibilityOverrides, updateDriverOnShift],
  );

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => {
        // Its dropdown lives on the status tab only, so it can't stay applied out of sight.
        if (v !== "pickupStatus" && selectedCategory !== "all")
          onFiltersChange({ category: "all" });
        setActiveTab(v as ScheduleTab);
      }}
      className="bg-white rounded-xl border"
    >
      <div className="flex items-end justify-between px-[15px] pt-[15px] border-b">
        <TabsList className="h-auto !items-end gap-0 rounded-none bg-transparent !p-0">
          <TabsTrigger value="pickupStatus" variant="folder" className={TAB_TRIGGER}>
            {t(Namespace.Routes, "pickupStatus")}
          </TabsTrigger>
          <TabsTrigger value="schedule" variant="folder" className={TAB_TRIGGER}>
            {t(Namespace.Routes, "schedule")}
          </TabsTrigger>
        </TabsList>
        {/* Centred on the whole header, as in Figma, not on the space below its top padding. */}
        <div className="-mt-[15px] flex items-center gap-8 self-center">
          <DatePicker
            showDayNav
            loading={dayLoading || movingWindow}
            className="!h-8 !px-2 !gap-1 text-text-body [&>svg]:!size-5"
            navButtonClassName={DAY_NAV_BUTTON}
            value={selectedDay}
            onChange={handleDayChange}
            maxDate={maxDay}
          />
          {activeTab !== "pickupStatus" && (
            <div
              role="group"
              aria-label={t(Namespace.Routes, "schedule")}
              className="w-16 shrink-0"
            >
              <ModeTabs
                variant="outline"
                value={ganttMode}
                onChange={setGanttMode}
                options={[
                  {
                    value: "truck",
                    label: t(Namespace.Routes, "ganttByTruck"),
                    icon: <IcGarbageTruck aria-hidden className="h-4 w-4" />,
                  },
                  {
                    value: "driver",
                    label: t(Namespace.Routes, "ganttByDriver"),
                    icon: <IcProfile aria-hidden className="h-4 w-4" />,
                  },
                ]}
              />
            </div>
          )}
          {/* Figma groups the dropdowns 8px apart; groups sit 32px apart. */}
          <div className="flex items-center gap-2">
            {activeTab === "pickupStatus" ? (
              <Select
                value={selectedCategory === "all" ? "all" : String(selectedCategory)}
                onValueChange={(v) =>
                  onFiltersChange({
                    category: v === "all" ? "all" : (Number(v) as ConnectProto.RouteCategory),
                  })
                }
              >
                <SelectTrigger className={cn("h-8", SELECT_FIGMA)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t(Namespace.Common, "all")}</SelectItem>
                  {categoryOptions.map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : ganttMode === "truck" ? (
              <Select
                value={String(truckTypeFilter)}
                onValueChange={(v) =>
                  setTruckTypeFilter(v === "all" ? "all" : (Number(v) as ConnectProto.TruckType))
                }
              >
                <SelectTrigger className={cn("h-8", SELECT_FIGMA)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t(Namespace.Common, "all")}</SelectItem>
                  {TRUCK_TYPE_ORDER.filter((type) => type !== ConnectProto.TruckType.UNDEFINED).map(
                    (type) => (
                      <SelectItem key={type} value={String(type)}>
                        {truckTypeLabel(type)}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            ) : (
              <Select value={activeTeamFilter} onValueChange={setTeamFilter}>
                <SelectTrigger className={cn("h-8 [&>span]:truncate", SELECT_FIGMA)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t(Namespace.Routes, "allTeams")}</SelectItem>
                  {teams.map((team) => (
                    <SelectItem key={String(team.id)} value={String(team.id)}>
                      {team.name}
                    </SelectItem>
                  ))}
                  <SelectItem value={NO_TEAM}>{t(Namespace.Common, "noTeam")}</SelectItem>
                </SelectContent>
              </Select>
            )}
            {divisions.length > 0 && (
              <Select
                value={divisionId === null ? "all" : String(divisionId)}
                onValueChange={(v) =>
                  onFiltersChange({ divisionId: v === "all" ? null : BigInt(v) })
                }
              >
                <SelectTrigger
                  className={cn("h-8 gap-2 whitespace-nowrap [&>span]:truncate", SELECT_FIGMA)}
                  title={divisions[selectedDivision]?.name}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t(Namespace.Routes, "pickupStatusTab.allDivisions")}
                  </SelectItem>
                  {divisions.map((org, i) => (
                    <SelectItem key={String(org.id)} value={String(org.id)} title={org.name}>
                      {divisionLabels[i]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? t(Namespace.Common, "collapse") : t(Namespace.Common, "expand")}
            className="transition-transform duration-200 hover:scale-110"
          >
            {isExpanded ? (
              <Minimize2 className="h-4 w-4 transition-all duration-300" />
            ) : (
              <Maximize2 className="h-4 w-4 transition-all duration-300" />
            )}
          </Button>
        </div>
      </div>
      <div ref={tabBodyRef} className="overflow-hidden rounded-b-xl">
        <TabsContent value="pickupStatus" className={cn("!mt-0", TAB_FADE)}>
          <PickupStatusPanel
            day={selectedDay}
            category={selectedCategory}
            divisionId={divisionId}
            organizationId={selectedCompanyId ?? null}
            fleet={pickupFleet}
            fleetError={fleetQuery.isError || shiftQuery.isError}
          />
        </TabsContent>
        <TabsContent
          value="schedule"
          forceMount
          className={cn("!mt-0 data-[state=inactive]:hidden", TAB_FADE)}
        >
          <div
            className={cn(
              "transition-all duration-500 ease-in-out overflow-hidden",
              isExpanded ? "h-[70vh]" : "h-[360px]",
            )}
          >
            <div
              className={cn(
                "h-full transition-opacity duration-300",
                ganttDimmed ? "opacity-50" : isExpanded ? "opacity-100" : "opacity-90",
              )}
              aria-busy={ganttDimmed || undefined}
            >
              <GanttSchedule
                ref={ganttRef}
                vehicles={layout.rows}
                items={layout.items}
                sections={layout.sections}
                labelWidth={(ganttMode === "driver" ? 220 : 140) + (isAiEnabled ? 32 : 0)}
                onItemClick={handleItemClick}
                onToggleAiVisibility={handleToggleAiVisibility}
                initialStart={initialWindow.start}
                initialEnd={initialWindow.end}
                onInitialDrawComplete={isToday ? undefined : noop}
                selectedItemId={infoType === "route" ? infoId : null}
                isAiEnabled={isAiEnabled}
                showMajorLabels
                shiftWord={t(Namespace.Routes, "shift")}
                aiHideTitle={t(Namespace.Routes, "hideFromAi")}
                aiShowTitle={t(Namespace.Routes, "showToAi")}
              />
            </div>
          </div>
        </TabsContent>
      </div>
    </Tabs>
  );
}

export default memo(RoutesSchedulePage);
